"use client"

import * as React from "react"
import { Button } from "./button"

interface TimePickerDialogProps {
  value?: string
  onChange?: (time: string) => void
  onClose?: () => void
  onDraggingChange?: (isDragging: boolean) => void
}

export function TimePickerDialog({ value, onChange, onClose, onDraggingChange }: TimePickerDialogProps) {
  const [isDragging, setIsDragging] = React.useState<"hour" | "minute" | "second" | null>(null)
  const [hasChanged, setHasChanged] = React.useState(false)

  // Parse initial value or use current time
  const parseTime = (timeString?: string) => {
    if (timeString) {
      const [h, m, s] = timeString.split(":")
      return {
        hour: Number.parseInt(h) || 0,
        minute: Number.parseInt(m) || 0,
        second: Number.parseInt(s) || 0,
      }
    }
    const now = new Date()
    return {
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    }
  }

  const initialTime = parseTime(value)
  const [hour, setHour] = React.useState(initialTime.hour)
  const [minute, setMinute] = React.useState(initialTime.minute)
  const [second, setSecond] = React.useState(initialTime.second)

  const hourRef = React.useRef<HTMLDivElement>(null)
  const minuteRef = React.useRef<HTMLDivElement>(null)
  const secondRef = React.useRef<HTMLDivElement>(null)

  const formatTime = (h: number, m: number, s: number) => {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  }

  const handleNow = () => {
    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()
    const currentSecond = now.getSeconds()

    setHour(currentHour)
    setMinute(currentMinute)
    setSecond(currentSecond)

    onChange?.(formatTime(currentHour, currentMinute, currentSecond))
    onClose?.()
  }

  const handleSave = () => {
    onChange?.(formatTime(hour, minute, second))
    onClose?.()
  }

  const calculateValue = (clientX: number, ref: React.RefObject<HTMLDivElement>, max: number) => {
    if (!ref.current) return 0
    const rect = ref.current.getBoundingClientRect()
    const x = clientX - rect.left
    const percentage = Math.max(0, Math.min(1, x / rect.width))
    return Math.round(percentage * max)
  }

  const handleMouseDown = (type: "hour" | "minute" | "second") => (e: React.MouseEvent) => {
    setIsDragging(type)
    setHasChanged(true)
    onDraggingChange?.(true)
    handleMouseMove(e, type)
  }

  const handleMouseMove = (e: React.MouseEvent | MouseEvent, type?: "hour" | "minute" | "second") => {
    const dragType = type || isDragging
    if (!dragType) return

    if (dragType === "hour") {
      const newHour = calculateValue(e.clientX, hourRef, 23)
      setHour(newHour)
    } else if (dragType === "minute") {
      const newMinute = calculateValue(e.clientX, minuteRef, 59)
      setMinute(newMinute)
    } else if (dragType === "second") {
      const newSecond = calculateValue(e.clientX, secondRef, 59)
      setSecond(newSecond)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(null)
    onDraggingChange?.(false)
  }

  React.useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e)
      const handleGlobalMouseUp = () => {
        setIsDragging(null)
        onDraggingChange?.(false)
      }

      document.addEventListener("mousemove", handleGlobalMouseMove)
      document.addEventListener("mouseup", handleGlobalMouseUp)

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove)
        document.removeEventListener("mouseup", handleGlobalMouseUp)
      }
    }
  }, [isDragging])

  const SliderRow = ({
    label,
    value,
    max,
    sliderRef,
    onMouseDown,
  }: {
    label: string
    value: number
    max: number
    sliderRef: React.RefObject<HTMLDivElement>
    onMouseDown: (e: React.MouseEvent) => void
  }) => {
    const percentage = (value / max) * 100

    return (
      <div className="flex items-center gap-3">
        <div className="w-5 text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">{label}</div>
        <div className="flex-1 relative min-w-0" ref={sliderRef}>
          <div
            className="h-1 rounded-full cursor-pointer"
            onMouseDown={onMouseDown}
            style={{
              background: `linear-gradient(to right, rgb(124, 58, 237) 0%, rgb(124, 58, 237) ${percentage}%, rgb(209, 213, 219) ${percentage}%, rgb(209, 213, 219) 100%)`,
            }}
          >
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full cursor-grab active:cursor-grabbing shadow-md"
              style={{ left: `${percentage}%`, transform: "translate(-50%, -50%)" }}
              onMouseDown={onMouseDown}
            />
          </div>
        </div>
        <div className="w-6 text-sm font-medium text-light-text-primary dark:text-dark-text-primary text-right">
          {value}
        </div>
      </div>
    )
  }

  return (
    <div className="w-[316px] p-4 bg-light-card-bg dark:bg-dark-card-bg border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
      <div className="space-y-4">
        {/* Time Display */}
        <div className="text-center">
          <div className="text-3xl font-bold text-primary">{formatTime(hour, minute, second)}</div>
        </div>

        {/* Sliders */}
        <div className="space-y-3 py-2">
          <SliderRow label="H" value={hour} max={23} sliderRef={hourRef} onMouseDown={handleMouseDown("hour")} />
          <SliderRow label="M" value={minute} max={59} sliderRef={minuteRef} onMouseDown={handleMouseDown("minute")} />
          <SliderRow label="S" value={second} max={59} sliderRef={secondRef} onMouseDown={handleMouseDown("second")} />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={handleSave}
            variant="default"
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white"
            disabled={!hasChanged}
          >
            Save
          </Button>
          <Button onClick={handleNow} variant="default" size="sm" className="bg-primary hover:bg-primary/90 text-white">
            Now
          </Button>
        </div>
      </div>
    </div>
  )
}
